import { flatMap, sumBy } from 'lodash';
import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { formatCurrency } from '../../../helpers/stringHelpers';
import { Expandable } from '../../../shared/Expandable';
import { ExpandControlChevron } from '../../../shared/ExpandControlChevron';
import { TextAreaField } from '../../../shared/form/inputs/TextAreaField';
import { Header3 } from '../../../shared/Headers';
import { Table, TBody, Td, THead, Tr } from '../../../shared/Table';
import { spacing01, spacing02, spacing04, spacing06 } from '../../../styling/spacing';
import { useBillOfMaterialsPricingModelInfo } from '../../bill-of-materials/BillOfMaterialsContext';
import { BillOfMaterialsItemsFieldNames } from '../../bill-of-materials/billOfMaterialsItemFormModel';
import {
  ActionButtonTh,
  CurrencyTd,
  CurrencyTh,
  DescriptionTd,
  DescriptionTh,
  DragHandleTh,
  QuantityTd,
  QuantityTh,
  UnitTd,
  UnitTh,
} from '../../bill-of-materials/BillOfMaterialsItemsTable';
import {
  BillOfMaterialsUnitDescriptionFieldNames,
  BillOfMaterialsUnitFormModel,
} from '../../bill-of-materials/billOfMaterialsUnitFormModel';
import {
  BillOfMaterialsPartsById,
  calculateTotalCost,
  calculateTotalPriceBeforeDiscount,
} from '../../bill-of-materials/dataForEditBillOfMaterialsForm';

type Props = {
  units: Array<BillOfMaterialsUnitFormModel>;
  itemsFieldName: BillOfMaterialsItemsFieldNames;
  descriptionFieldName: BillOfMaterialsUnitDescriptionFieldNames;
  partsById: BillOfMaterialsPartsById;
};

export const BillOfMaterialsUnitsTable = ({ units, descriptionFieldName, itemsFieldName, partsById }: Props) => {
  const { isUsingLegacyPricingModel, selectedCustomerTypeOption } = useBillOfMaterialsPricingModelInfo();

  const [isExpanded, setIsExpanded] = useState(false);

  const totalQuantity = sumBy(units, unit => unit.quantity);

  const totalPriceBeforeDiscount = calculateTotalPriceBeforeDiscount(
    isUsingLegacyPricingModel,
    flatMap(units, unit => unit[itemsFieldName]),
    partsById,
    selectedCustomerTypeOption,
  );

  const totalCost = calculateTotalCost(
    flatMap(units, unit => unit[itemsFieldName]),
    partsById,
  );

  return (
    <UnitsTableContainer>
      <BillOfMaterialsUnitsTableHeader>
        <ExpandControlChevron isExpanded={isExpanded} onClick={() => setIsExpanded(!isExpanded)} />
        <Header3>Units Table</Header3>
      </BillOfMaterialsUnitsTableHeader>
      <Expandable isExpanded={isExpanded} animationDurationSeconds={0.5}>
        <StyledTable>
          <THead>
            <Tr>
              <DragHandleTh />
              <UnitTh>Unit</UnitTh>
              <QuantityTh>Quantity</QuantityTh>
              <DescriptionTh>Description</DescriptionTh>
              <CurrencyTh>Cost per unit (£)</CurrencyTh>
              <CurrencyTh>Price per unit (£)</CurrencyTh>
              <CurrencyTh>Cost (£)</CurrencyTh>
              <CurrencyTh>Price (£)</CurrencyTh>
              <ActionButtonTh />
            </Tr>
          </THead>
          <TBody>
            {units.map((unit, unitIndex) => {
              const totalUnitPriceBeforeDiscount = calculateTotalPriceBeforeDiscount(
                isUsingLegacyPricingModel,
                unit[itemsFieldName],
                partsById,
                selectedCustomerTypeOption,
              );

              const totalUnitCost = calculateTotalCost(unit[itemsFieldName], partsById);

              return (
                <Tr key={unit.name}>
                  <Td />
                  <UnitTd>{unit.name}</UnitTd>
                  <QuantityTd>{unit.quantity}</QuantityTd>
                  <DescriptionTd>
                    <TextAreaField name={`units.${unitIndex}.${descriptionFieldName}`} label={null} />
                  </DescriptionTd>
                  <CurrencyTd>{formatCurrency(totalUnitCost / unit.quantity)}</CurrencyTd>
                  <CurrencyTd>{formatCurrency(totalUnitPriceBeforeDiscount / unit.quantity)}</CurrencyTd>
                  <CurrencyTd>{formatCurrency(totalUnitCost)}</CurrencyTd>
                  <CurrencyTd>{formatCurrency(totalUnitPriceBeforeDiscount)}</CurrencyTd>
                  <Td />
                </Tr>
              );
            })}
            <Tr>
              <Td />
              <UnitTd />
              <QuantityTd>{totalQuantity}</QuantityTd>
              <DescriptionTd />
              <CurrencyTd />
              <CurrencyTd />
              <CurrencyTd>{formatCurrency(totalCost)}</CurrencyTd>
              <CurrencyTd>{formatCurrency(totalPriceBeforeDiscount)}</CurrencyTd>
              <Td />
            </Tr>
          </TBody>
        </StyledTable>
      </Expandable>
    </UnitsTableContainer>
  );
};

const UnitsTableContainer = styled.div`
  padding: ${spacing04} 0;
`;

const BillOfMaterialsUnitsTableHeader = styled.div`
  display: flex;
  align-items: center;

  ${Header3} {
    margin: 0 0 ${spacing01} ${spacing02};
  }
`;

const StyledTable = styled(Table)`
  table-layout: fixed;
  margin: ${spacing04} 0 ${spacing06} 0;
`;
